import { pt, norm } from './layout';
import type { Person, ChartSettings } from './types';

export interface LabelLine { className: string; dy: number; text: string | undefined; style: string }
export interface ResolvedLabel {
  hidden: boolean; anchor: 'start' | 'end' | 'middle'; x: number; y: number; rotation: number;
  leaderLine: { x1: number; y1: number; x2: number; y2: number } | null; lines: LabelLine[];
}

export function resolvePersonLabel(
  person: Person, px: number, py: number, refX: number, refY: number,
  sunX: number, sunY: number, settings: ChartSettings,
): ResolvedLabel {
  const mode = person.labelMode && person.labelMode !== 'inherit' ? person.labelMode : settings.labelMode;
  if (mode === 'hidden') {
    return { hidden: true, anchor: 'start', x: px, y: py, rotation: 0, leaderLine: null, lines: [] };
  }

  const rot = person.labelRot || 0;
  const a = (Math.atan2(py - refY, px - refX) * 180) / Math.PI;
  const R = settings.dotSize;
  let ax = px, ay = py, deg = rot;
  let anchor: 'start' | 'end' | 'middle' = 'start';
  const dist = person.labelDist != null ? person.labelDist : settings.labelDist;
  let leaderLine: { x1: number; y1: number; x2: number; y2: number } | null = null;

  if (mode === 'auto') {
    const dx = px - refX, dy = py - refY;
    const rr = Math.hypot(dx, dy) || 1;
    const side = person.labelSide || 'auto';
    let isRight: boolean;
    if (side === 'left') isRight = false;
    else if (side === 'right') isRight = true;
    else if (side === 'outward') isRight = dx >= 0;
    else if (side === 'inward') isRight = dx < 0;
    else if (Math.abs(dx) > 0.3 * rr) isRight = dx > 0;
    else { const cosA = Math.cos(Math.atan2(py - sunY, px - sunX)); isRight = Math.abs(cosA) > 0.3 ? cosA > 0 : dx > 0; }
    anchor = isRight ? 'start' : 'end';
    ax = px + (isRight ? dist + R : -(dist + R));
    ay = py;
    leaderLine = { x1: px + (isRight ? R + 1 : -(R + 1)), y1: py, x2: ax + (isRight ? -6 : 6), y2: py };
  } else if (mode === 'radial') {
    const flip = Math.abs(norm(a)) > 90;
    const p0 = pt(R + dist * 0.45, a, px, py);
    ax = p0.x; ay = p0.y; anchor = flip ? 'end' : 'start';
    deg = (flip ? a + 180 : a) + rot;
  } else if (mode === 'tangent') {
    const p0 = pt(R + dist * 0.6, a, px, py);
    let t = a + 90;
    if (Math.abs(norm(t)) > 90) t -= 180;
    ax = p0.x; ay = p0.y; anchor = 'middle'; deg = t + rot;
  } else if (mode === 'above') { ax = px; ay = py - (R + 16); anchor = 'middle'; }
  else if (mode === 'below') { ax = px; ay = py + (R + 12); anchor = 'middle'; }

  const nmStyle = (person.labelColor ? `fill:${person.labelColor};` : '') + (person.labelSize ? `font-size:${person.labelSize}px;` : '');
  const rlStyle = (person.labelColor ? `fill:${person.labelColor};opacity:.75;` : '') + (person.labelSize ? `font-size:${Math.max(6, person.labelSize - 1.5)}px;` : '');

  const lines: LabelLine[] = [];
  if (person.vacant) {
    lines.push({ className: 'nm vac', dy: -7, text: 'Vacant', style: nmStyle });
    if (settings.showRoles) lines.push({ className: 'rl vac', dy: 8, text: person.role, style: rlStyle });
    if (person.req) lines.push({ className: 'req', dy: settings.showRoles ? 22 : 8, text: person.req, style: '' });
  } else {
    lines.push({ className: 'nm', dy: settings.showRoles ? -4 : 4, text: person.name, style: nmStyle || (person.lead ? 'fill:var(--gold)' : '') });
    if (settings.showRoles) lines.push({ className: 'rl', dy: 11, text: person.role, style: rlStyle });
  }

  return { hidden: false, anchor, x: ax, y: ay, rotation: deg, leaderLine, lines };
}
